// A resumed recording is several MediaRecorder WebM files back to back, each
// restarting its clock at 0, so decoders overlap them and drop the later audio.
// Mediabunny copies the encoded packets into one WebM with a continuous
// timeline (no re-encode). Anything unexpected returns the input unchanged.

const EBML_ID = 0x1a45dfa3;
const DOC_TYPE_ID = 0x4282;

type Bytes = Uint8Array<ArrayBuffer>;

// Just enough EBML to recognise a WebM header; Mediabunny parses the rest.
const readVint = (
  b: Bytes,
  pos: number,
  keepMarker: boolean
): { value: number; length: number } | null => {
  const first = b[pos];
  if (first === undefined || first === 0) return null;
  const length = Math.clz32(first) - 23;
  if (length > 8 || pos + length > b.length) return null;
  let value = keepMarker ? first : first & (0xff >> length);
  for (let i = 1; i < length; i++) value = value * 256 + b[pos + i];
  return { value, length };
};

const isWebmHeaderAt = (b: Bytes, pos: number): boolean => {
  const id = readVint(b, pos, true);
  if (id?.value !== EBML_ID) return false;
  const size = readVint(b, pos + id.length, false);
  if (!size || size.value > 64) return false;
  const end = pos + id.length + size.length + size.value;
  let at = pos + id.length + size.length;
  while (at < end && end <= b.length) {
    const childId = readVint(b, at, true);
    const childSize = childId && readVint(b, at + childId.length, false);
    if (!childId || !childSize) return false;
    const dataStart = at + childId.length + childSize.length;
    if (childId.value === DOC_TYPE_ID) {
      return (
        String.fromCharCode(
          ...b.subarray(dataStart, dataStart + childSize.value)
        ) === "webm"
      );
    }
    at = dataStart + childSize.value;
  }
  return false;
};

const findRecordingStarts = (b: Bytes): number[] => {
  const starts: number[] = [];
  for (let i = 0; i + 4 <= b.length; i++) {
    if (
      b[i] === 0x1a &&
      b[i + 1] === 0x45 &&
      b[i + 2] === 0xdf &&
      b[i + 3] === 0xa3 &&
      isWebmHeaderAt(b, i)
    ) {
      starts.push(i);
    }
  }
  return starts;
};

const descriptionBytes = (d: AudioDecoderConfig["description"]): string =>
  d
    ? (ArrayBuffer.isView(d)
        ? new Uint8Array(d.buffer, d.byteOffset, d.byteLength)
        : new Uint8Array(d)
      ).join(",")
    : "";

const sameDecoderConfig = (a: AudioDecoderConfig, b: AudioDecoderConfig): boolean =>
  a.codec === b.codec &&
  a.sampleRate === b.sampleRate &&
  a.numberOfChannels === b.numberOfChannels &&
  descriptionBytes(a.description) === descriptionBytes(b.description);

/** Returns the recording as one continuous WebM, or the input unchanged. */
export const joinWebmRecordings = async (blob: Blob): Promise<Blob> => {
  if (!blob.type.includes("webm")) return blob;
  try {
    const starts = findRecordingStarts(new Uint8Array(await blob.arrayBuffer()));
    if (starts.length < 2 || starts[0] !== 0) return blob;

    // Loaded only when a resumed recording actually needs joining.
    const {
      BlobSource,
      BufferTarget,
      EncodedAudioPacketSource,
      EncodedPacketSink,
      Input,
      Output,
      WEBM,
      WebMOutputFormat,
    } = await import("mediabunny");

    const target = new BufferTarget();
    const output = new Output({ format: new WebMOutputFormat(), target });
    let source: InstanceType<typeof EncodedAudioPacketSource> | null = null;
    let firstConfig: AudioDecoderConfig | null = null;
    let configSent = false;
    let timelineEnd = 0;

    for (let i = 0; i < starts.length; i++) {
      const input = new Input({
        formats: [WEBM],
        source: new BlobSource(blob.slice(starts[i], starts[i + 1] ?? blob.size)),
      });
      try {
        const track = await input.getPrimaryAudioTrack();
        const codec = await track?.getCodec();
        const config = await track?.getDecoderConfig();
        if (!track || !codec || !config) {
          if (source) await output.cancel();
          return blob;
        }

        if (!source || !firstConfig) {
          source = new EncodedAudioPacketSource(codec);
          output.addAudioTrack(source);
          await output.start();
          firstConfig = config;
        } else if (!sameDecoderConfig(config, firstConfig)) {
          console.warn(
            "[webmJoin] Recordings use different codec settings; not joined."
          );
          await output.cancel();
          return blob;
        }

        let shift: number | null = null;
        let copied = 0;
        try {
          for await (const packet of new EncodedPacketSink(track).packets()) {
            // Starts this recording where the previous one ended.
            shift ??= timelineEnd - packet.timestamp;
            const timestamp = packet.timestamp + shift;
            await source.add(
              packet.clone({ timestamp }),
              configSent ? undefined : { decoderConfig: config }
            );
            configSent = true;
            timelineEnd = Math.max(timelineEnd, timestamp + packet.duration);
            copied++;
          }
        } catch (err) {
          // A crash can cut the last write short; keep what was read.
          if (copied === 0) throw err;
        }
      } finally {
        input.dispose();
      }
    }

    await output.finalize();
    if (!target.buffer) return blob;
    return new Blob([target.buffer], { type: blob.type });
  } catch (err) {
    console.warn("[webmJoin] Couldn't join recordings; sending as captured.", err);
    return blob;
  }
};
